// LegendSwatch — pure presentational primitive used by every schedule
// legend (ScheduleLegend's type+severity strip, DensityHeatmap's
// volume+severity+today footer). The shape discriminator (not just a
// fill/ring boolean) makes the brand "type colour ≤8px dot, never a
// fill" rule mechanical: type entries use shape="dot", severity and
// volume entries use shape="block", and the Today indicator uses
// shape="ring". A future entry that tries to express type as a block
// fails the type system at the call site if we narrow tone unions.

import type { ReactElement } from "react";

export type SwatchTone =
  // Type / accent colours — only valid with shape="dot".
  | "amber"
  | "red"
  | "brand"
  | "emerald"
  | "sky"
  | "fuchsia"
  | "teal"
  // Density volume ramp (the same .d-N classes the cell uses).
  | "volume-1"
  | "volume-2"
  | "volume-3"
  | "volume-4";

export type SwatchShape = "dot" | "block" | "ring";

export interface LegendSwatchProps {
  label: string;
  shape: SwatchShape;
  tone: SwatchTone;
  testId?: string;
}

// .d-1..d-4 are single-sourced in index.css; rendering them through a
// span here means the legend volume swatch is byte-equivalent with the
// heatmap cell tint.
const VOLUME_CLASS: Record<
  "volume-1" | "volume-2" | "volume-3" | "volume-4",
  string
> = {
  "volume-1": "d-1",
  "volume-2": "d-2",
  "volume-3": "d-3",
  "volume-4": "d-4",
};

const COLOUR_BG: Record<
  "amber" | "red" | "brand" | "emerald" | "sky" | "fuchsia" | "teal",
  string
> = {
  amber: "bg-amber-500",
  red: "bg-red-500",
  brand: "bg-brand-500",
  emerald: "bg-emerald-500",
  sky: "bg-sky-500",
  fuchsia: "bg-fuchsia-500",
  teal: "bg-teal-500",
};

const COLOUR_RING: Record<
  "amber" | "red" | "brand" | "emerald" | "sky" | "fuchsia" | "teal",
  string
> = {
  amber: "ring-amber-500",
  red: "ring-red-500",
  brand: "ring-brand-500",
  emerald: "ring-emerald-500",
  sky: "ring-sky-500",
  fuchsia: "ring-fuchsia-500",
  teal: "ring-teal-500",
};

function isVolumeTone(
  tone: SwatchTone,
): tone is "volume-1" | "volume-2" | "volume-3" | "volume-4" {
  return tone.startsWith("volume-");
}

function renderShape(shape: SwatchShape, tone: SwatchTone): ReactElement {
  if (shape === "dot") {
    // ≤8px filled circle — type-colour entries only.
    const cls = isVolumeTone(tone)
      ? VOLUME_CLASS[tone]
      : COLOUR_BG[tone];
    return (
      <span aria-hidden="true" className={`h-2 w-2 rounded-full ${cls}`} />
    );
  }
  if (shape === "ring") {
    // Outline-only square — Today indicator on the density heatmap.
    const cls = isVolumeTone(tone) ? "" : COLOUR_RING[tone];
    return (
      <span
        aria-hidden="true"
        className={`h-3 w-3 rounded-sm ring-2 ring-inset ${cls}`}
      />
    );
  }
  // block — small filled square (~12px). Volume ramp + severity overlays.
  const cls = isVolumeTone(tone) ? VOLUME_CLASS[tone] : COLOUR_BG[tone];
  return (
    <span aria-hidden="true" className={`h-3 w-3 rounded-sm ${cls}`} />
  );
}

export default function LegendSwatch({
  label,
  shape,
  tone,
  testId,
}: LegendSwatchProps) {
  return (
    <span
      data-testid={testId}
      data-shape={shape}
      className="inline-flex items-center gap-1"
    >
      {renderShape(shape, tone)}
      <span className="text-caption text-slate-600 dark:text-slate-400">
        {label}
      </span>
    </span>
  );
}
